import { useAudio } from '../context/AudioContext';
import { Play, Pause, SkipBack, SkipForward, Repeat, Repeat1, ChevronDown, ChevronUp, X, Volume2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Capacitor } from '@capacitor/core';

// Bottom padding to account for native AdMob banner (50-60px)
const ADMOB_BANNER_HEIGHT = 60;

const AudioPlayer = () => {
  const {
    isPlaying,
    currentVerse,
    currentReciter,
    reciters,
    progress,
    duration,
    continuousPlay,
    togglePlayPause,
    playNext,
    playPrevious,
    changeReciter,
    toggleContinuousPlay,
    seek
  } = useAudio();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  
  // Check if running in native app (where AdMob banner will be shown)
  const isNativeApp = Capacitor.isNativePlatform();
  const bottomOffset = isNativeApp ? ADMOB_BANNER_HEIGHT : 0;

  if (!currentVerse) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const handleReopen = () => {
    setIsDismissed(false);
    setIsMinimized(false);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Floating reopen button when dismissed - positioned on LEFT to avoid badge */}
      {isDismissed && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          onClick={handleReopen}
          style={{ bottom: `${24 + bottomOffset}px` }}
          className="fixed left-6 z-50 w-16 h-16 rounded-full bg-[#3A5A40] hover:bg-[#2B4330] text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="reopen-player-btn"
          title="Open audio player"
        >
          <Volume2 size={28} strokeWidth={1.5} />
        </motion.button>
      )}

      {/* Audio Player */}
      <AnimatePresence>
        {!isDismissed && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            style={{ bottom: `${bottomOffset}px` }}
            className="audio-player fixed left-0 right-0 z-50"
            data-testid="audio-player"
          >
            <div className="max-w-4xl mx-auto px-6">
              {/* Minimized View */}
              {isMinimized ? (
                <div className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={togglePlayPause}
                      className="w-9 h-9 rounded-full bg-[#3A5A40] hover:bg-[#2B4330] text-white flex items-center justify-center active:scale-95 transition-all"
                      data-testid="audio-play-pause-btn-mini"
                    >
                      {isPlaying ? <Pause size={16} strokeWidth={1.5} fill="currentColor" /> : <Play size={16} strokeWidth={1.5} fill="currentColor" />}
                    </button>
                    <div className="text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Surah {currentVerse.chapterId} · Ayah {currentVerse.verseNumber}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsMinimized(false)}
                      className="text-[#6B726B] hover:text-[#3A5A40] p-1 transition-colors"
                      title="Expand player"
                      data-testid="expand-player-btn"
                    >
                      <ChevronUp size={20} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={handleDismiss}
                      className="text-[#6B726B] hover:text-red-500 p-1 transition-colors"
                      title="Close player"
                      data-testid="close-player-btn"
                    >
                      <X size={20} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ) : (
                /* Full View */
                <div className="py-4">
                  {/* Header with minimize/close buttons */}
                  <div className="flex justify-end gap-2 mb-2">
                    <button
                      onClick={() => setIsMinimized(true)}
                      className="text-[#6B726B] hover:text-[#3A5A40] p-1 transition-colors"
                      title="Minimize player"
                      data-testid="minimize-player-btn"
                    >
                      <ChevronDown size={20} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={handleDismiss}
                      className="text-[#6B726B] hover:text-red-500 p-1 transition-colors"
                      title="Close player"
                      data-testid="close-player-btn-full"
                    >
                      <X size={20} strokeWidth={1.5} />
                    </button>
                  </div>
              {/* Progress Bar */}
              <div className="mb-3">
                <Slider
                  value={[progress]}
                  max={duration || 100}
                  step={1}
                  onValueChange={(value) => seek(value[0])}
                  className="w-full"
                  data-testid="audio-progress-slider"
                />
                <div className="flex justify-between text-xs text-[#6B726B] mt-1">
                  <span>{formatTime(progress)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between gap-4">
                {/* Verse Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Surah {currentVerse.chapterId} · Ayah {currentVerse.verseNumber}
                  </div>
                  <div className="text-xs text-[#6B726B]">{currentReciter?.reciter_name || 'Loading...'}</div>
                </div>

                {/* Playback Controls */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={playPrevious}
                    className="text-[#3A5A40] hover:text-[#2B4330] active:scale-95 transition-all"
                    data-testid="audio-previous-btn"
                  >
                    <SkipBack size={20} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={togglePlayPause}
                    className="w-10 h-10 rounded-full bg-[#3A5A40] hover:bg-[#2B4330] text-white flex items-center justify-center active:scale-95 transition-all"
                    data-testid="audio-play-pause-btn"
                  >
                    {isPlaying ? <Pause size={20} strokeWidth={1.5} fill="currentColor" /> : <Play size={20} strokeWidth={1.5} fill="currentColor" />}
                  </button>
                  <button
                    onClick={playNext}
                    className="text-[#3A5A40] hover:text-[#2B4330] active:scale-95 transition-all"
                    data-testid="audio-next-btn"
                  >
                    <SkipForward size={20} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={toggleContinuousPlay}
                    className={`ml-2 ${continuousPlay ? 'text-[#3A5A40]' : 'text-[#6B726B]'} hover:text-[#3A5A40] active:scale-95 transition-all`}
                    data-testid="audio-continuous-btn"
                    title={continuousPlay ? "Continuous play ON" : "Continuous play OFF"}
                  >
                    {continuousPlay ? <Repeat size={20} strokeWidth={1.5} /> : <Repeat1 size={20} strokeWidth={1.5} />}
                  </button>
                </div>

                {/* Reciter Selector */}
                <div className="flex-1 flex justify-end">
                  <Select value={currentReciter?.id?.toString()} onValueChange={(value) => changeReciter(value)}>
                    <SelectTrigger className="w-[180px] border-[#E8EAE8] bg-white" data-testid="reciter-select">
                      <SelectValue placeholder="Select Reciter" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E8EAE8]">
                      {reciters.map((reciter) => (
                        <SelectItem key={reciter.id} value={reciter.id} data-testid={`reciter-option-${reciter.id}`}>
                          {reciter.reciter_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
  </>
  );
};

export default AudioPlayer;